import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Grid,
  styled,
  Fab,
  Chip,
  Button,
  Stack,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Footer from '../../components/Footer/footer';
import { apigetPublic, apidelete } from '../../service/api';
import DashboardFilterBar, { DashboardNoMatches } from '../../components/dashboard/DashboardFilterBar';
import useDashboardFilters from '../../components/dashboard/useDashboardFilters';

const byTitle = (a, b) => String(a.title || '').localeCompare(String(b.title || ''));
const newestFirst = (field) => (a, b) => new Date(b[field] || 0) - new Date(a[field] || 0);

// Builder (in the title), state and loan officer are searchable rather than
// separate filters.
const FILTER_CONFIG = {
  search: (i) => [
    i.title,
    i.slug,
    i.state,
    ...(i.loanOfficers || []),
    ...(i.agentNames || []),
    ...(i.communities || []),
  ],
  filters: [
    { key: 'status', label: 'Status', values: (i) => [i.isActive ? 'Active' : 'Inactive'] },
  ],
  sorts: [
    { key: 'title', label: 'Title A–Z', compare: byTitle },
    { key: 'title-desc', label: 'Title Z–A', compare: (a, b) => byTitle(b, a) },
    { key: 'updated', label: 'Recently updated', compare: newestFirst('updatedAt') },
    { key: 'newest', label: 'Newest added', compare: newestFirst('createdAt') },
    {
      key: 'communities',
      label: 'Most communities',
      compare: (a, b) => (b.communities?.length || 0) - (a.communities?.length || 0) || byTitle(a, b),
    },
  ],
  defaultSort: 'title',
};


const DashboardContainer = styled(Container)(() => ({
  paddingTop: '8px',
  paddingBottom: '50px',
}));

const IntakeCard = styled(Card)(({ theme }) => ({
  height: '100%',
  boxShadow: theme.shadows[3],
  borderRadius: '16px',
  overflow: 'hidden',
  transition: 'transform 0.3s, box-shadow 0.3s',
  '&:hover': {
    transform: 'translateY(-6px)',
    boxShadow: theme.shadows[6],
  },
}));

const IntakeLink = styled(Link)(() => ({
  textDecoration: 'none',
  color: 'inherit',
}));

const IntakeImage = styled(CardMedia)(() => ({
  height: 160,
  backgroundSize: 'contain',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  backgroundColor: '#f5f5f5',
}));

export default function BuilderIntakeDashboard() {
  const [intakes, setIntakes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  const fetchIntakes = async () => {
    const result = await apigetPublic('intake');
    if (result?.status === 200 && Array.isArray(result.data?.result)) {
      setIntakes(result.data.result);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchIntakes();
  }, []);

  const { visible, total, active, clear, barProps } = useDashboardFilters(intakes, FILTER_CONFIG);

  const normalizeUrl = (url) => {
    if (!url) return '/assets/images/no-image.jpg';
    return url.startsWith('http')
      ? url
      : `https://${url.replace(/^https?:\/\//, '')}`;
  };

  const handleDelete = async (e, intakeId) => {
    e.preventDefault();
    e.stopPropagation();

    if (!window.confirm('Delete this intake form?')) return;

    try {
      const res = await apidelete(`intake/${intakeId}`);
      if (res?.status === 200) {
        toast.success('Intake deleted');
        fetchIntakes();
      }
    } catch (err) {
      toast.error('Failed to delete intake');
    }
  };

  return (
    <>

      <Box
        sx={{
          backgroundImage: 'url(/assets/backgroundimageforherobanner.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          minHeight: '100vh',
        }}
      >
        <Box
          sx={{
            backgroundColor: 'rgba(255,255,255,0.9)',
            minHeight: '100vh',
            position: 'relative',
          }}
        >
          <DashboardContainer>
            <Typography variant="h4" gutterBottom sx={{ mb: 2 }}>
              Builder Intake Dashboard
            </Typography>

            <DashboardFilterBar
              noun="intake forms"
              {...barProps}
              shown={visible.length}
              total={total}
              active={active}
              onClear={clear}
            />

            {isLoading ? (
              <Typography align="center">Loading intake forms...</Typography>
            ) : intakes.length > 0 && visible.length === 0 ? (
              <DashboardNoMatches noun="intake forms" onClear={clear} />
            ) : intakes.length > 0 ? (
              <Grid container spacing={4}>
                {visible.map((intake) => (
                  <Grid item xs={12} sm={6} md={4} key={intake._id}>
                    <IntakeLink to={`/dashboard/intake/${intake._id}`}>
                      <IntakeCard>
                        <IntakeImage image={normalizeUrl(intake.logoSrc)} />

                        <CardContent>
                          <Typography variant="h6" sx={{ mb: 1 }}>
                            {intake.title}
                          </Typography>

                          <Typography variant="body2" color="text.secondary">
                            Slug: {intake.slug}
                          </Typography>

                          <Typography variant="body2" color="text.secondary">
                            Loan Officers: {intake.loanOfficers?.length || 0}
                          </Typography>

                          <Typography variant="body2" color="text.secondary">
                            Agents: {intake.agentNames?.length || 0}
                          </Typography>

                          <Typography variant="body2" color="text.secondary">
                            Communities: {intake.communities?.length || 0}
                          </Typography>

                          <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                            <Chip
                              size="small"
                              label={intake.isActive ? 'Active' : 'Inactive'}
                              color={intake.isActive ? 'success' : 'default'}
                            />

                            <Button
                              size="small"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                navigate(`/dashboard/intake/${intake._id}`);
                              }}
                            >
                              Edit
                            </Button>

                            <Button
                              size="small"
                              color="error"
                              onClick={(e) => handleDelete(e, intake._id)}
                            >
                              Delete
                            </Button>
                          </Stack>
                        </CardContent>
                      </IntakeCard>
                    </IntakeLink>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Typography align="center" sx={{ mt: 4 }}>
                No builder intake forms found.
              </Typography>
            )}
          </DashboardContainer>

          <Fab
            color="primary"
            aria-label="add"
            onClick={() => navigate('/dashboard/intake/create')}
            sx={{
              position: 'fixed',
              bottom: 32,
              right: 32,
              backgroundColor: '#1976d2',
              '&:hover': { backgroundColor: '#115293' },
            }}
          >
            <AddIcon />
          </Fab>
        </Box>
      </Box>

      <Box sx={(theme) => ({ ...theme.mixins.toolbar })} />
      <Footer />
    </>
  );
}
